import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card } from "../ui/card";
import { CreditCard, Lock, Loader2 } from "lucide-react";
import { initializePaddle, Paddle } from "@paddle/paddle-js";
import { env } from "@/env";
import { useSession } from "@/lib/auth-client";
import { useTRPC } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useRouter } from "next/navigation";

interface PaymentStepProps {
  planId?: string;
  onComplete?: () => void;
}

export function PaymentStep({ planId: propPlanId, onComplete }: PaymentStepProps) {
  const [paddle, setPaddle] = useState<Paddle>();
  const session = useSession();
  const trpc = useTRPC();
  const searchParams = useSearchParams();
  const router = useRouter();
  
  // Get planId from query params or prop
  const planId = searchParams.get("planId") || propPlanId || "";

  const { data: plans, isLoading: plansLoading } = useQuery(trpc.subscriptions.getPlans.queryOptions());
  
  const selectedPlan = plans?.find((plan) => plan.priceId === planId);

  const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000';
  }

   useEffect(() => {
      initializePaddle({
        checkout: {
          settings: {
            variant: 'one-page',
            displayMode: 'inline',
            frameInitialHeight: 450,
            frameTarget: 'paddle-payment-form',
            frameStyle: 'width: 100%; background-color: transparent; border: none',
          }
        },
        environment: env.NEXT_PUBLIC_PADDLE_ENVIRONMENT,
        token: env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN,
      }).then((paddleInstance) => {
        if (paddleInstance) {
          setPaddle(paddleInstance);
 paddle?.Checkout.open({

      settings: {
        allowLogout: false,
        successUrl: `${getBaseUrl()}/dashboard?subscription=success&plan=${planId}`,
        onComplete: () => {
          if (onComplete) {
            onComplete();
          }
          router.replace("/dashboard");
        },
      },
      items: [{ priceId: planId, quantity: 1 }],
      customer: {
        id: session!.data!.user.customerId!,
      },
      customData: {
        userId: session!.data!.user.id,
        isNewUser: true,
      },

    });
        }
      }).catch(() => {
        console.log('Paddle initialization failed');
      });
    }, [paddle?.Initialized, planId]);

  if (plansLoading || !selectedPlan) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl w-full mx-auto space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-mono">Payment Details</h1>
        <p className="text-muted-foreground">
          Complete your subscription to get started
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className={'paddle-payment-form'}></div>

        <div className="space-y-6">
          <Card className="p-6 space-y-4">
            <h3 className="font-mono">Order Summary</h3>
            
            <div className="space-y-3 pt-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-mono">{selectedPlan.name}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Billing</span>
                <span className="font-mono">Monthly</span>
              </div>

              <div className="pt-3 border-t border-border flex justify-between">
                <span className="font-mono">Total</span>
                <span className="text-xl font-mono">${selectedPlan.price}/mo</span>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-muted/30">
            <div className="space-y-2">
              <h4 className="text-sm font-mono">What happens next?</h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex gap-2">
                  <span>•</span>
                  <span>You'll be charged ${selectedPlan.price} today</span>
                </li>
                <li className="flex gap-2">
                  <span>•</span>
                  <span>Access to your dashboard immediately</span>
                </li>
                <li className="flex gap-2">
                  <span>•</span>
                  <span>Cancel anytime from your account settings</span>
                </li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
